import React, { useEffect, useState, useMemo } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart3, Loader2, RefreshCw, Users, Eye, TrendingUp, Globe, Link as LinkIcon, Languages } from "lucide-react";

const RANGES = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

/** Cheap country-code → flag emoji converter (regional indicator symbols). */
function flag(cc) {
  if (!cc || cc.length !== 2) return "🌍";
  const A = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(A + (cc.charCodeAt(0) - a), A + (cc.charCodeAt(1) - a));
}

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data: json } = await http.get(`/admin/analytics?days=${d}`);
      setData(json);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  const trendMax = useMemo(() => {
    if (!data?.trend?.length) return 1;
    return Math.max(1, ...data.trend.map((r) => r.views));
  }, [data]);

  return (
    <section
      id="analytics"
      data-testid="admin-analytics-panel"
      className="clay p-5 sm:p-6 space-y-5"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-8 h-8 rounded-full bg-[color:var(--navy)]/10 text-[color:var(--navy)] grid place-items-center">
          <BarChart3 className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="eyebrow text-[color:var(--navy)]">Visitor analytics</p>
          <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>
            Who is visiting Popoyo.app
          </h3>
        </div>
        <div className="flex items-center gap-1.5 bg-white/70 rounded-full p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setDays(r.key)}
              data-testid={`analytics-range-${r.key}`}
              className={`text-xs px-3 py-1 rounded-full font-semibold transition ${
                days === r.key
                  ? "bg-[color:var(--navy)] text-white"
                  : "text-[color:var(--ink-2)] hover:text-[color:var(--navy)]"
              }`}
            >
              {r.label}
            </button>
          ))}
          <Button
            onClick={() => load(days)}
            variant="ghost"
            className="pill h-7 px-2 text-[color:var(--ink-2)]"
            data-testid="analytics-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<Eye className="w-4 h-4" />}
          label="Today · views"
          value={data?.today?.views ?? "—"}
          hint={`${data?.today?.visitors ?? 0} visitors`}
        />
        <StatCard
          icon={<Users className="w-4 h-4" />}
          label={`${days}d · unique`}
          value={data?.totals?.visitors ?? "—"}
          hint={`${data?.totals?.views ?? 0} total views`}
        />
        <StatCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Avg / day"
          value={data?.trend?.length
            ? Math.round((data?.totals?.views || 0) / Math.max(1, data.trend.length))
            : "—"}
          hint="views per day"
        />
        <StatCard
          icon={<Globe className="w-4 h-4" />}
          label="Countries"
          value={data?.top_countries?.length ?? "—"}
          hint="represented"
        />
      </div>

      {/* Trend chart */}
      <div>
        <p className="text-xs text-[color:var(--ink-2)] mb-1.5">Daily views (last {days} days)</p>
        <div className="flex items-end gap-1 h-24 bg-white/60 rounded-2xl px-3 py-3 overflow-x-auto thin-scroll">
          {(data?.trend || []).map((r) => (
            <div
              key={r.date}
              title={`${r.date}: ${r.views} views · ${r.visitors} visitors`}
              className="shrink-0 w-3 sm:w-4 rounded-t bg-[color:var(--coral)]/80 hover:bg-[color:var(--coral)] transition"
              style={{ height: `${Math.max(4, (r.views / trendMax) * 100)}%` }}
            />
          ))}
          {(!data?.trend || data.trend.length === 0) && (
            <p className="text-xs text-[color:var(--ink-2)] italic">
              No visits recorded yet. Page-views start collecting as soon as visitors browse the site.
            </p>
          )}
        </div>
      </div>

      {/* Two-column: top pages + top referrers */}
      <div className="grid md:grid-cols-2 gap-4">
        <ListCard
          title="Top pages"
          icon={<LinkIcon className="w-3.5 h-3.5" />}
          rows={(data?.top_pages || []).slice(0, 8).map((p) => ({
            label: p.path || "/",
            value: p.views,
            sub: `${p.visitors} unique`,
          }))}
        />
        <ListCard
          title="Top referrers"
          icon={<TrendingUp className="w-3.5 h-3.5" />}
          rows={(data?.top_referrers || []).slice(0, 8).map((r) => ({
            label: r.source || "Direct",
            value: r.views,
            sub: "views",
          }))}
        />
      </div>

      {/* Two-column: top countries + top langs */}
      <div className="grid md:grid-cols-2 gap-4">
        <ListCard
          title="Top countries"
          icon={<Globe className="w-3.5 h-3.5" />}
          rows={(data?.top_countries || []).slice(0, 8).map((c) => ({
            label: `${flag(c.country)} ${c.country}`,
            value: c.views,
            sub: `${c.visitors} unique`,
          }))}
        />
        <ListCard
          title="Top languages"
          icon={<Languages className="w-3.5 h-3.5" />}
          rows={(data?.top_langs || []).slice(0, 8).map((l) => ({
            label: l.lang,
            value: l.views,
            sub: "views",
          }))}
        />
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, hint }) {
  return (
    <div className="clay p-3 bg-white/70" data-testid={`analytics-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[color:var(--ink-2)] text-[11px] uppercase tracking-wider">
        {icon} {label}
      </div>
      <p className="text-2xl font-bold text-[color:var(--navy)] tabular-nums" style={{ fontFamily: "Outfit" }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-[11px] text-[color:var(--ink-2)]">{hint}</p>
    </div>
  );
}

function ListCard({ title, icon, rows }) {
  return (
    <div className="clay p-3 bg-white/70">
      <p className="eyebrow flex items-center gap-1.5 mb-2 text-[color:var(--navy)]">
        {icon} {title}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-[color:var(--ink-2)] italic">No data yet.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {rows.map((r, i) => (
            <li key={i} className="flex items-baseline gap-2">
              <span className="text-[color:var(--ink-2)] tabular-nums text-xs w-5">{i + 1}.</span>
              <span className="flex-1 truncate">{r.label}</span>
              <span className="tabular-nums font-semibold">{r.value.toLocaleString()}</span>
              <span className="text-[10px] text-[color:var(--ink-2)]">{r.sub}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
